/* eslint-disable no-unused-vars */
import React, { useState } from "react"
import { PebbleTable } from "../../ui/TableComponents";
import { useNavigate } from "react-router-dom";
import placeholderImage from "../../../assets/images/eventImages/companyLogo.jpg"
import { useBreadcrumb } from "../../../context/BreadCrumbContext";
import { updateMPSApplication } from "../../../firebaseFunctions/updateMPSApplication";

const MarketplaceSellers = ({ mpsRegistrations = [], sellerCapacity = "0", reload }) => {
    const navigate = useNavigate();
    const { setBreadcrumb } = useBreadcrumb();
    const [boothModalOpen, setBoothModalOpen] = useState(false);
    const [boothMode, setBoothMode] = useState("manual"); // "manual" | "auto"
    const [boothEdits, setBoothEdits] = useState({});
    const [saving, setSaving] = useState(false);
    const [approving, setApproving] = useState(null); // vendorId being actioned

    const pendingVendors = mpsRegistrations.filter(v => v.applicationStatus?.toLowerCase() === "applied");

    const handleQuickApprove = async (vendorId) => {
        setApproving(vendorId);
        try {
            await updateMPSApplication({ applicationStatus: "Approved" }, vendorId);
            if (reload) reload();
        } catch (err) {
            console.error("Error approving vendor:", err);
        } finally {
            setApproving(null);
        }
    };

    const handleQuickDecline = async (vendorId) => {
        setApproving(vendorId + "_decline");
        try {
            await updateMPSApplication({ applicationStatus: "Declined" }, vendorId);
            if (reload) reload();
        } catch (err) {
            console.error("Error declining vendor:", err);
        } finally {
            setApproving(null);
        }
    };

    const columns = [
        {
            header: "",
            key: "image",
            type: "image"
        },
        {
            header: "Company",
            key: "company",
            type: "compound",
            primaryKey: "company",
            secondaryKey: "date"
        },
        {
            header: "Business Type",
            key: "businessType",
            type: "text"
        },
        {
            header: "Status",
            key: "status",
            type: "status",
            statusType: "application"
        },
        {
            header: "Payment",
            key: "payment",
            type: "status",
            statusType: "payment"
        },
        {
            header: "Contact",
            key: "contact",
            type: "compound",
            primaryKey: "phone",
            secondaryKey: "email"
        },
        {
            header: "Booth",
            key: "boothNumber",
            type: "text",
        },
        {
            header: "",
            key: "action",
            type: "button",
            onClick: (id) => handleViewEvent(id),
        }
    ];

    const mapMPSDataToTableData = (data) => {
        const now = new Date();
        return data.map((item) => {
            const startDateTime = new Date(`${item.startDate}T${item.startTime}`);
            const endDateTime = new Date(`${item.endDate}T${item.endTime}`);

            let eventStatus = "";
            if (item.isDraft) {
                eventStatus = "Draft";
            } else if (startDateTime > now) {
                eventStatus = "Upcoming";
            } else if (startDateTime <= now && endDateTime >= now) {
                eventStatus = "Ongoing";
            } else if (endDateTime < now) {
                eventStatus = "Archived";
            }

            return {
                id: item.id,
                image: item?.companyLogoURL || placeholderImage,
                company: item?.businessName,
                businessType: item?.businessType,
                status: item?.applicationStatus,
                payment: item?.paymentStatus,
                phone: item?.businessPhoneNumber,
                email: item?.businessEmail,
                boothNumber: item?.boothNumber || (
                    (item?.applicationStatus?.toLowerCase() === "approved" || item?.applicationStatus?.toLowerCase() === "registered")
                        ? "⚠ Unassigned"
                        : "-"
                ),
                action: "View",
                date: formatEventDateRange(item?.datesApplyingFor),
            };
        });
    };

    const handleViewEvent = (id) => {
        const event = mpsRegistrations?.find((e) => e.id === id);
        if (event) {
            setBreadcrumb({ eventName: event.businessName });
            navigate(`/eo/marketplace/${id}`);
        }
    };

    // Only approved/registered vendors get booth assignments
    const boothVendors = mpsRegistrations.filter(v => {
        const s = v.applicationStatus?.toLowerCase();
        return s === "approved" || s === "registered";
    });

    // Open booth assignment modal, pre-populate edits from existing data
    const openBoothModal = () => {
        const initial = {};
        boothVendors.forEach(v => {
            initial[v.id] = v.boothNumber || "";
        });
        setBoothEdits(initial);
        setBoothMode("manual");
        setBoothModalOpen(true);
    };

    // Auto-assign: fill sequential numbers for vendors that don't have one yet
    const handleAutoAssign = () => {
        const updated = { ...boothEdits };
        let next = 1;
        // Find the highest existing number to start after it
        Object.values(updated).forEach(val => {
            const n = parseInt(val);
            if (!isNaN(n) && n >= next) next = n + 1;
        });
        boothVendors.forEach(v => {
            if (!updated[v.id]) {
                updated[v.id] = String(next++);
            }
        });
        setBoothEdits(updated);
    };

    // Auto-assign all: overwrite everything with fresh sequential numbers
    const handleAutoAssignAll = () => {
        const updated = {};
        boothVendors.forEach((v, i) => {
            updated[v.id] = String(i + 1);
        });
        setBoothEdits(updated);
    };

    const handleSaveBooths = async () => {
        setSaving(true);
        try {
            const updates = boothVendors
                .filter(v => boothEdits[v.id] !== undefined)
                .map(v => updateMPSApplication({ boothNumber: boothEdits[v.id] || "" }, v.id));
            await Promise.all(updates);
            setBoothModalOpen(false);
            if (reload) reload();
        } catch (err) {
            console.error("Error saving booth numbers:", err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="w-full p-4 pt-2">
            <div className="flex items-center justify-between mb-5 pl-1 pr-1 sm:pr-8 sm:pl-8">
                <div className="flex items-center gap-5">
                    <p className="text-xl font-medium text-grey-900">Marketplace Vendors</p>
                    <p className="pt-1 text-sm text-grey-900">
                        <span>( {mpsRegistrations?.length}</span>
                        <span> {mpsRegistrations?.length === 1 ? "vendor" : "vendors"} / </span>
                        <span>{sellerCapacity}</span>
                        <span> )</span>
                    </p>
                </div>
                {mpsRegistrations.length > 0 && (
                    <button
                        onClick={openBoothModal}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-grey-700 border border-grey-300 rounded-lg hover:bg-grey-50 transition"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        Assign Booths
                    </button>
                )}
            </div>
            {/* Pending applications panel */}
            {pendingVendors.length > 0 && (
                <div className="mx-1 sm:mx-8 mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
                        <p className="text-sm font-semibold text-amber-900">
                            {pendingVendors.length} application{pendingVendors.length !== 1 ? "s" : ""} waiting for review
                        </p>
                    </div>
                    <div className="space-y-2">
                        {pendingVendors.map(vendor => (
                            <div key={vendor.id} className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-amber-100 gap-4">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-grey-900 truncate">{vendor.businessName}</p>
                                    <p className="text-xs text-grey-500">{vendor.businessType}</p>
                                </div>
                                <div className="flex gap-2 flex-shrink-0">
                                    <button
                                        onClick={() => handleQuickDecline(vendor.id)}
                                        disabled={approving !== null}
                                        className="px-3 py-1.5 text-xs border border-red-300 text-red-600 hover:bg-red-50 rounded-lg transition font-medium disabled:opacity-40"
                                    >
                                        Decline
                                    </button>
                                    <button
                                        onClick={() => handleQuickApprove(vendor.id)}
                                        disabled={approving !== null}
                                        className="px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 text-white rounded-lg transition font-medium disabled:opacity-40 min-w-[70px]"
                                    >
                                        {approving === vendor.id ? "..." : "Approve"}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <PebbleTable
                columns={columns}
                data={mapMPSDataToTableData(mpsRegistrations)}
            />

            {/* Booth Assignment Modal */}
            {boothModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
                        {/* Header */}
                        <div className="p-6 border-b border-grey-200 flex items-center justify-between flex-shrink-0">
                            <div>
                                <h3 className="text-lg font-semibold text-grey-900">Assign Booth Numbers</h3>
                                <p className="text-sm text-grey-500 mt-0.5">{boothVendors.length} approved vendor{boothVendors.length !== 1 ? "s" : ""}</p>
                            </div>
                            <button
                                onClick={() => setBoothModalOpen(false)}
                                className="p-1.5 hover:bg-grey-100 rounded-lg transition"
                            >
                                <svg className="w-5 h-5 text-grey-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Mode toggle + auto actions */}
                        <div className="px-6 py-4 border-b border-grey-100 flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setBoothMode("manual")}
                                    className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${boothMode === "manual" ? "bg-grey-900 text-white" : "text-grey-600 hover:bg-grey-100"}`}
                                >
                                    Manual
                                </button>
                                <button
                                    onClick={() => setBoothMode("auto")}
                                    className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${boothMode === "auto" ? "bg-grey-900 text-white" : "text-grey-600 hover:bg-grey-100"}`}
                                >
                                    Auto
                                </button>
                                {boothMode === "auto" && (
                                    <div className="flex items-center gap-2 ml-2">
                                        <button
                                            onClick={handleAutoAssign}
                                            className="px-3 py-1.5 text-sm bg-grey-100 text-grey-700 rounded-lg hover:bg-grey-200 transition"
                                        >
                                            Fill empty
                                        </button>
                                        <button
                                            onClick={handleAutoAssignAll}
                                            className="px-3 py-1.5 text-sm bg-grey-100 text-grey-700 rounded-lg hover:bg-grey-200 transition"
                                        >
                                            Reassign all
                                        </button>
                                    </div>
                                )}
                            </div>
                            {boothMode === "auto" && (
                                <p className="text-xs text-grey-500 mt-2">
                                    "Fill empty" assigns sequential numbers to unassigned vendors. "Reassign all" overwrites everything starting from 1.
                                </p>
                            )}
                        </div>

                        {/* Vendor list */}
                        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-2">
                            {boothVendors.length === 0 && (
                                <p className="text-sm text-grey-500 text-center py-6">No approved vendors yet. Approve applications first before assigning booths.</p>
                            )}
                            {boothVendors.map((vendor) => (
                                <div key={vendor.id} className="flex items-center justify-between gap-4 py-2 border-b border-grey-100 last:border-0">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-grey-900 truncate">{vendor.businessName}</p>
                                        <p className="text-xs text-grey-500">{vendor.businessType}</p>
                                    </div>
                                    <input
                                        type="text"
                                        value={boothEdits[vendor.id] ?? ""}
                                        onChange={e => setBoothEdits(prev => ({ ...prev, [vendor.id]: e.target.value }))}
                                        placeholder="—"
                                        className="w-20 px-2 py-1.5 text-sm text-center border border-grey-300 rounded-lg focus:ring-2 focus:ring-grey-900 focus:border-transparent"
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-grey-200 flex gap-3 flex-shrink-0">
                            <button
                                onClick={() => setBoothModalOpen(false)}
                                className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveBooths}
                                disabled={saving}
                                className="flex-1 px-4 py-2 bg-grey-900 text-white rounded-lg hover:bg-grey-800 transition font-medium disabled:opacity-50"
                            >
                                {saving ? "Saving..." : "Save Assignments"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MarketplaceSellers;


const formatEventDateRange = (dates) => {
    if (!dates || dates.length === 0) return "";

    const formatDate = (date) => {
        const monthNames = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        const day = date.getDate();
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();

        const getOrdinalSuffix = (n) => {
            if (n >= 11 && n <= 13) return `${n}th`;
            switch (n % 10) {
                case 1: return `${n}st`;
                case 2: return `${n}nd`;
                case 3: return `${n}rd`;
                default: return `${n}th`;
            }
        };

        return `${month} ${getOrdinalSuffix(day)} ${year}`;
    };

    const sortedDates = dates.map((date) => new Date(date)).sort((a, b) => a - b);
    const dateRanges = [];
    let currentRange = [sortedDates[0]];

    for (let i = 1; i < sortedDates.length; i++) {
        const previousDate = sortedDates[i - 1];
        const currentDate = sortedDates[i];
        const isContinuous = (currentDate - previousDate) / (1000 * 60 * 60 * 24) === 1;

        if (isContinuous) {
            currentRange.push(currentDate);
        } else {
            dateRanges.push(currentRange);
            currentRange = [currentDate];
        }
    }
    dateRanges.push(currentRange);

    const formattedRanges = dateRanges.map((range) => {
        if (range.length === 1) {
            return formatDate(range[0]);
        } else {
            const start = range[0];
            const end = range[range.length - 1];
            if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
                return `${formatDate(start).split(" ")[0]} ${formatDate(start).split(" ")[1]} - ${formatDate(end).split(" ")[1]} ${formatDate(end).split(" ")[2]}`;
            } else if (start.getFullYear() === end.getFullYear()) {
                return `${formatDate(start).split(" ")[0]} ${formatDate(start).split(" ")[1]} - ${formatDate(end).split(" ")[0]} ${formatDate(end).split(" ")[1]} ${formatDate(end).split(" ")[2]}`;
            } else {
                return `${formatDate(start)} - ${formatDate(end)}`;
            }
        }
    });

    return formattedRanges.join(", ");
};
